import { useState } from 'react';
import { MASKED_SECRET } from '@glyph/protocol-ts';
import { deriveApSsid } from '@glyph/protocol-ts';
import type { NetworkSettings } from '@glyph/protocol-ts';
import { isLocalShell, useClient, useCloudStatus, useDevice, useNetwork, useSaveSettings } from '@glyph/core';
import { UnitHotspotPassword } from './HotspotPassword';
import {
  Button,
  Card,
  CardHeader,
  FactRow,
  Field,
  KeyValueRow,
  ListRow,
  Note,
  PageHeader,
  Section,
  SegmentedControl,
  StatusPill,
  Switch,
  SwitchRow,
  TextInput,
  useToast,
} from '@glyph/ui';

export interface NetworkScreenProps {
  deviceId?: string;
}

/** Network: hotspot (SSID suffix/password/band/clients/URLs), uplink (Ethernet/Wi-Fi/USB·LTE failover), Cloud relay. */
export function NetworkScreen({ deviceId }: NetworkScreenProps) {
  const dev = useDevice(deviceId);
  const net = useNetwork(deviceId);
  const cloud = useCloudStatus(deviceId);
  const s = useSaveSettings(deviceId);
  const toast = useToast();
  const local = isLocalShell(useClient());
  const [showPass, setShowPass] = useState(false);
  const n = s.draft?.network;
  const st = net.data;
  if (!n) return <Section />;
  const up = (p: Partial<NetworkSettings>) => s.update('network', p);
  const dirty = s.isDirty('network');
  const save = async () => {
    await s.save(['network']);
    toast.push('Network settings applied · hotspot restarts', 'ok');
  };
  const passMasked = n.ap_password === MASKED_SECRET;
  const ssid = st?.ap.ssid ?? deriveApSsid(s.draft?.device.name ?? '', n.ap_ssid_suffix);
  // Cloud console: the password the cloud set (the unit only ever reports it masked), with its delivery state; the
  // unit's own page keeps the plain field.
  const passInput = !local ? (
    <UnitHotspotPassword deviceId={deviceId ?? ''} />
  ) : (
    <div className="flex min-w-0 gap-1.5">
      <TextInput
        variant="mono"
        type={showPass ? 'text' : 'password'}
        value={passMasked ? '' : n.ap_password}
        placeholder={passMasked ? '•••••••• (set)' : '8–63 characters'}
        onChange={(e) => up({ ap_password: e.target.value })}
        className="min-w-0 flex-1"
      />
      <Button size="responsive" variant="ghost" mono onClick={() => setShowPass(!showPass)}>
        {showPass ? 'HIDE' : 'SHOW'}
      </Button>
    </div>
  );
  const bandSeg = (fw?: boolean) => (
    <SegmentedControl
      fullWidth={fw}
      value={n.ap_band}
      onChange={(v) => up({ ap_band: v })}
      options={[
        { value: '5', label: '5 GHz' },
        { value: '2.4', label: '2.4 GHz' },
      ]}
    />
  );
  const uplinkRows = [
    {
      k: 'Ethernet',
      st: st?.eth.state,
      v: st?.eth.state === 'active' ? `Active · ${st.eth.speed ?? ''}` : 'No link',
      sub: st?.eth.address
        ? `${st.eth.address} · gw ${st.eth.gateway ?? '—'} · DNS ${st.eth.dns.join(', ') || '—'}`
        : 'cable not detected',
    },
    {
      k: 'Wi-Fi',
      st: st?.wifi.state,
      v:
        st?.wifi.state === 'active'
          ? `Active · ${st.wifi.ssid}`
          : st?.wifi.ssid
            ? `Standby · ${st.wifi.ssid}`
            : 'No network saved',
      sub: st?.wifi.ssid
        ? `saved · ${st.wifi.rssi_dbm ?? '—'} dBm · takes over if Ethernet drops`
        : 'add a network to enable failover',
    },
    {
      k: 'LTE / tether',
      st: st?.lte.state,
      v:
        st?.lte.state === 'active'
          ? `Active · ${st.lte.apn ?? ''}`
          : n.lte_enabled
            ? 'Standby'
            : 'Not connected',
      sub: 'phone, 4G dongle or LTE HAT · last resort',
    },
  ];
  const stPill = (x?: string) => (
    <StatusPill tone={x === 'active' ? 'ok' : 'fg5'}>{x === 'active' ? 'ACTIVE' : 'IDLE'}</StatusPill>
  );
  const c = cloud.data;
  const relayRows = c && (
    <>
      <FactRow label="Endpoint" value={`${c.url.replace(/^wss?:\/\//, '')} · WSS`} />
      <FactRow
        label="Heartbeat"
        value={c.connected ? `${c.heartbeat_s} s · RTT ${c.rtt_ms ?? '—'} ms` : 'not connected'}
        valueTone={c.connected ? undefined : 'warn'}
      />
      <FactRow label="Auth" value="Ed25519 challenge-response" />
      <FactRow label="Audio monitor stream" value="Opus 24 kb/s · on demand" />
      <FactRow label="Reconnects · 24 h" value={String(c.reconnects_24h)} />
    </>
  );
  const urls = st?.ap.urls ?? [];
  const clients = st?.ap.clients ?? [];

  return (
    <Section>
      <PageHeader
        desktopOnly
        title="Network"
        meta={`${dev.data?.name ?? ''} · uplink order Ethernet → Wi-Fi → LTE / tether`}
        actions={
          <Button variant="primary" disabled={!dirty || s.isSaving} onClick={() => void save()}>
            {dirty ? 'Apply' : 'Applied'}
          </Button>
        }
      />
      {/* mobile */}
      <div className="flex flex-col gap-4 md:hidden">
        <Card>
          <CardHeader
            title="Local hotspot"
            actions={
              <Switch checked={n.ap_enabled} onChange={(v) => up({ ap_enabled: v })} label="Hotspot" />
            }
          />
          <ListRow label="SSID" title={ssid} sub="Glyph-<device name>-<suffix>" />
          <ListRow
            title="Suffix"
            trailing={
              <TextInput
                value={n.ap_ssid_suffix}
                onChange={(e) => up({ ap_ssid_suffix: e.target.value })}
                className="w-[140px]"
                placeholder="auto"
              />
            }
          />
          <div className="flex flex-col gap-2.5 border-t border-line px-4 pb-3.5 pt-3">
            <span className="text-lg text-fg">Password</span>
            {passInput}
          </div>
          <div className="flex flex-col gap-2.5 border-t border-line px-4 pb-3.5 pt-3">
            <span className="text-lg text-fg">Band</span>
            {bandSeg(true)}
          </div>
          <SwitchRow
            title="Hidden network"
            hint="SSID not broadcast · shown on the OLED"
            checked={n.ap_hidden}
            onChange={(v) => up({ ap_hidden: v })}
          />
          {urls.map((u, i) => (
            <FactRow key={u} label={i === 0 ? 'Local UI' : 'Also on uplink'} value={u} />
          ))}
          <FactRow label="Clients" value={clients.map((x) => x.host).join(' · ') || '—'} />
        </Card>
        <Card>
          <CardHeader title="Uplink" />
          {uplinkRows.map((r) => (
            <ListRow key={r.k} label={r.k.toUpperCase()} title={r.v} sub={r.sub} trailing={stPill(r.st)} />
          ))}
        </Card>
        <Card>
          <CardHeader title="Cloud relay" />
          {relayRows}
        </Card>
        <Button size="lg" variant="primary" disabled={!dirty || s.isSaving} onClick={() => void save()}>
          {dirty ? 'Apply network settings' : 'Applied'}
        </Button>
      </div>
      {/* desktop */}
      <div
        className="hidden items-start gap-5 md:grid"
        style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}
      >
        <Card mode="padded">
          <SwitchRow
            first
            title="Local hotspot"
            checked={n.ap_enabled}
            onChange={(v) => up({ ap_enabled: v })}
            className="border-t-0 py-0 [&>div>span:first-child]:text-md [&>div>span:first-child]:font-medium"
          />
          <div className="grid grid-cols-2 gap-3">
            <Field label="SSID suffix" right={<span className="font-mono text-xs text-fg4">{ssid}</span>}>
              <TextInput
                block
                value={n.ap_ssid_suffix}
                onChange={(e) => up({ ap_ssid_suffix: e.target.value })}
                placeholder="auto"
              />
            </Field>
            <Field label="Password">{passInput}</Field>
          </div>
          <Field label="Band">{bandSeg()}</Field>
          <SwitchRow
            first
            title="Hidden network"
            hint="SSID not broadcast · shown on the OLED"
            checked={n.ap_hidden}
            onChange={(v) => up({ ap_hidden: v })}
            className="border-t-0 py-0"
          />
          <div className="flex flex-col">
            {urls.map((u, i) => (
              <FactRow inset key={u} label={i === 0 ? 'Local UI' : 'Also on uplink'} value={u} />
            ))}
            <FactRow inset label="Clients" value={clients.map((x) => x.host).join(' · ') || '—'} />
          </div>
          {!n.ap_enabled && (
            <Note tone="warn">
              With the hotspot off the local UI is only reachable over the uplink address.
            </Note>
          )}
        </Card>
        <Card>
          <div className="flex flex-col gap-[5px] p-4">
            <span className="text-md font-medium leading-none">Uplink</span>
            <span className="font-mono text-xs text-fg4">
              fails over automatically · Deepgram and relay reconnect in under 2 s
            </span>
          </div>
          {uplinkRows.map((r) => (
            <KeyValueRow key={r.k} k={r.k} value={r.v} sub={r.sub} trailing={stPill(r.st)} />
          ))}
        </Card>
        <Card>
          <div className="flex flex-col gap-[5px] p-4">
            <span className="text-md font-medium leading-none">Cloud relay</span>
            <span className="font-mono text-xs text-fg4">
              device dials out · nothing inbound is ever opened
            </span>
          </div>
          {relayRows}
        </Card>
      </div>
    </Section>
  );
}
